import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import { getData, getDataAdmin } from '../../services/FetchNodeServices';

export default function Sidebar({ activeKey, onMenuClick, isOpen, closeSidebar }) {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(
    sessionStorage.getItem('isAuthenticated') === 'true'
  );

  const [user, setUser] = useState(() => {
    const storedUser = sessionStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [rolesData, setRolesData] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [companySettings, setCompanySettings] = useState('');

  // ✅ All sidebar menu items
  const menuItems = [
    { key: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line', path: '/dashboard', module: 'Dashboard', roles: ['admin', 'distributor', 'retailer','superStockist', 'promoter'] },
    { key: 'users', label: 'User Management', icon: 'ri-user-line', path: '/users', module: 'User Management', roles: ['admin', 'distributor','superStockist', 'TSM-ASM'] },
    { key: 'staff', label: 'Staff Management', icon: 'ri-team-line', path: '/staff', module: 'Staff Management', roles: ['admin'] },
    { key: 'claims', label: 'Claims Management', icon: 'ri-file-shield-line', path: '/claims', module: 'Claims Management', roles: ['admin'] },
    { key: 'products', label: 'Products', icon: 'ri-product-hunt-line', path: '/products', module: 'Products', roles: ['admin'] },
    { key: 'amcs', label: 'WEC Management', icon: 'ri-file-shield-line', path: '/amcs', module: 'WEC Management', roles: ['admin', 'distributor', 'retailer', 'superStockist', 'promoter'] },
    { key: 'customers', label: 'Customers', icon: 'ri-user-heart-line', path: '/customers', module: 'Customers', roles: ['admin'] },
    { key: 'wallet', label: 'Wallet', icon: 'ri-wallet-line', path: '/wallet', module: 'Wallet Management', roles: ['admin', 'distributor', 'retailer', 'superStockist', 'TSM-ASM'] },
    { key: 'reports', label: 'Reports', icon: 'ri-bar-chart-line', path: '/reports', module: 'Reports', roles: ['admin', 'distributor'] },
    { key: 'settings', label: 'Settings', icon: 'ri-settings-line', path: '/settings', module: 'Settings', roles: ['admin'] },
    // { key: 'alladmin', label: 'All Admin Management', icon: 'ri-shield-user-line', path: '/alladmin', module: 'Alladmin', roles: ['admin'] },
  ];

  // ✅ Fetch all roles and permissions
  const fetchRoles = async () => {
    try {
      const response = await getData('api/role/get-all-roles');
      if (response?.status === true) {
        setRolesData(response.data);

        // Find the logged-in user's role info (for staff roles)
        const matchedRole = response.data.find(r => r.name === user?.role);

        // Collect modules that have "read" permission
        const readModules = matchedRole?.permissions
          ?.filter(p => p.permissions.includes('read'))
          ?.map(p => p.module) || [];

        setUserPermissions(readModules);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  useEffect(() => {
    if (user && !['admin', 'distributor', 'retailer', 'superStockist', 'TSM-ASM', 'promoter'].includes(user.role)) {
      // Only fetch for dynamic staff roles
      fetchRoles();
    }
  }, [user]);

  // ✅ Filter menu items
  const filteredMenuItems = menuItems.filter(item => {
    // If user is admin/distributor/retailer → use static role-based visibility
    if (['admin', 'distributor', 'retailer'].includes(user?.role)) {
      return item.roles.includes(user.role);
    }

    // Otherwise → use dynamic permission-based filtering
    return userPermissions.includes(item.module);
  });

  // ✅ Logout
  const handleLogout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.setItem('isAuthenticated', 'false');
    navigate('/');
    setIsAuthenticated(false);
  };



  const fetchTeamAndConditions = async () => {
    try {
      const response2 = await getData(`api/company/get-company-settings`);
      const response = await getDataAdmin('api/company/get-AMC-settings');
      console.log("response==>get-team-and-conditions=>", response)
      // if (response?.status === true) {
      //   setTeamAndConditions(response?.data);
      //   // setAmcPercentage(response?.data?.defaultPercentage);
      // }
      if (response2?.status === true) {
        setCompanySettings(response2?.data);
      }
    } catch (error) {
      console.error('Error fetching team and conditions:', error);
    }
  }

  useEffect(() => {
    fetchTeamAndConditions();
  }, [])

  return (
    <>
      {/* ⭐ Mobile Overlay */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-40 z-40 md:hidden transition-opacity duration-300 
        ${isOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}
        onClick={closeSidebar}
      ></div>

      {/* ⭐ Sidebar Box */}
      <div
        className={clsx(
          "bg-gray-900 text-white w-64 h-screen flex flex-col z-50 fixed md:static top-0 left-0 transition-transform duration-300",
          isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        {/* Header with centered logo, company name and admin panel text */}
        <div className="p-4 border-b border-gray-700 flex-shrink-0 relative">
          {/* ❌ Close button only for mobile - positioned absolute */}
          <button onClick={closeSidebar} className="md:hidden text-white text-2xl absolute top-4 right-4">
            ✕
          </button>

          <div className="flex flex-col items-center text-center">
            {companySettings?.logo ? (
              <img src={companySettings.logo} className="h-32 w-32 object-contain mb-2" alt="Company Logo" />
            ) : (
              <i className="ri-file-shield-line text-gray-400 text-7xl mb-2"></i>
            )}
            <h1 className="text-lg font-bold text-white">
              {companySettings?.name || 'KVS'}
            </h1>
            <p className="text-xs text-gray-400">Admin Panel</p>
          </div>
        </div>

        {/* Menu - scrollbar hidden on desktop */}
        <nav className="flex-1 p-4 overflow-y-auto sidebar-scroll">
          <ul className="space-y-2">
            {filteredMenuItems.map(item => (
              <li key={item.key}>
                <button
                  onClick={() => onMenuClick(item.key, item.path)}
                  className={clsx(
                    "w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors cursor-pointer",
                    activeKey === item.key
                      ? "bg-blue-600 text-white"
                      : "text-gray-300 hover:bg-gray-800 hover:text-white"
                  )}
                >
                  <i className={`${item.icon} mr-3 w-5`}></i>
                  <span className="text-sm">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700">
          <p className="font-medium">{user?.name}</p>
          <p className="text-xs text-gray-400 mb-3">{user?.email}</p>

          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg"
          >
            Logout
          </button>
        </div>
      </div>
    </>
  );
}

